import CrmHeader from "@/components/crm/CrmHeader";
import CrmMain from "@/components/crm/CrmMain";
import AddOpportunityDialog from "@/components/crm/AddOpportunityDialog";
import { LossReasonDialog } from "@/components/crm/LossReasonDialog";
import { WonOpportunityDialog } from "@/components/crm/WonOpportunityDialog";
import { LoadingProgress } from "@/components/crm/LoadingProgress";
import React, { useState, useEffect, useCallback } from "react";
import { usePipelinesSettings, type PipelineStage } from "@/hooks/usePipelinesSettings";
import { useOpportunities, type DateFilterPeriod, type WonOpportunityData } from "@/hooks/useOpportunities";
import { useAuth } from "@/contexts/AuthContext";
import { Plus } from "lucide-react";
import type { OpportunityFilters, OpportunityStatusFilter } from "@/components/ui/shared/MoreFilterCRM";
import { saveSelectedPipeline, getSelectedPipeline } from "@/utils/sessionStorage";
import { DEFAULT_CRM_DATE_FIELD } from "@/lib/crmDateFilter";
import { useTranslation } from "react-i18next";

const CRM_DATE_FILTER_KEY = 'crm_date_filter';

const getSavedDateFilter = (accountId: string): DateFilterPeriod => {
  try {
    const saved = sessionStorage.getItem(`${CRM_DATE_FILTER_KEY}_${accountId}`);
    if (saved && ['30d', '60d', '90d', 'all'].includes(saved)) {
      return saved as DateFilterPeriod;
    }
  } catch {}
  return '30d';
};

const saveDateFilter = (accountId: string, period: DateFilterPeriod) => {
  try {
    sessionStorage.setItem(`${CRM_DATE_FILTER_KEY}_${accountId}`, period);
  } catch {}
};

const Crm: React.FC = () => {
  const { t } = useTranslation("crm");
  const { user } = useAuth();
  const { pipelines, stages, loading: pipelinesLoading } = usePipelinesSettings();
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<OpportunityStatusFilter>('all');
  const [dateFilter, setDateFilter] = useState<DateFilterPeriod>(() =>
    user?.account_id ? getSavedDateFilter(user.account_id) : '30d'
  );
  const [filters, setFilters] = useState<OpportunityFilters>({
    tags: [],
    minPrice: null,
    maxPrice: null,
    dateFrom: null,
    dateTo: null,
    dateField: DEFAULT_CRM_DATE_FIELD,
    attributionId: null,
    status: 'all'
  });

  const {
    opportunities,
    loading: opportunitiesLoading,
    loadingProgress,
    moveOpportunity,
    createOpportunity,
    findExistingOpportunities,
    closeOpportunity,
    reopenOpportunity,
    deleteOpportunity,
    updateOpportunity,
    reorderOpportunities
  } = useOpportunities(selectedPipelineId, statusFilter, dateFilter, filters.dateField);

  const handleDateFilterChange = (period: DateFilterPeriod) => {
    setDateFilter(period);
    if (user?.account_id) {
      saveDateFilter(user.account_id, period);
    }
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [lossReasonDialogOpen, setLossReasonDialogOpen] = useState(false);
  const [pendingLossData, setPendingLossData] = useState<{
    opportunityId: string;
    stageId: string;
    opportunityTitle?: string;
  } | null>(null);

  const [wonDialogOpen, setWonDialogOpen] = useState(false);
  const [pendingWonData, setPendingWonData] = useState<{
    opportunityId: string;
    stageId: string;
    opportunityTitle?: string;
    initialProduct?: string;
    initialPrice?: number;
  } | null>(null);

  const handleFilterChange = (newFilters: OpportunityFilters) => {
    setFilters(newFilters);
    if (newFilters.status !== statusFilter) {
      setStatusFilter(newFilters.status);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      tags: [],
      minPrice: null,
      maxPrice: null,
      dateFrom: null,
      dateTo: null,
      dateField: DEFAULT_CRM_DATE_FIELD,
      attributionId: null,
      status: 'all'
    });
    setStatusFilter('all');
  };

  const handleMoveOpportunity = useCallback(async (
    opportunityId: string,
    newStageId: string,
    targetStage?: PipelineStage,
    orderIndex?: number
  ) => {
    if (!targetStage) {
      return false;
    }

    const opportunity = opportunities.find(o => o.id === opportunityId);
    const isCurrentlyArchived = opportunity?.status === 'won' || opportunity?.status === 'lost';
    const isTargetWon = targetStage.stage_type === 'won';
    const isTargetLost = targetStage.stage_type === 'lost';
    const isTargetNormal = targetStage.stage_type === 'normal';

    if (isCurrentlyArchived && isTargetNormal) {
      return reopenOpportunity(opportunityId, newStageId);
    }

    if (isTargetWon) {
      setPendingWonData({
        opportunityId,
        stageId: newStageId,
        opportunityTitle: opportunity?.contact_name || 'Contato sem nome',
        initialProduct: opportunity?.product,
        initialPrice: opportunity?.price
      });
      setWonDialogOpen(true);
      return false;
    }

    if (isTargetLost) {
      setPendingLossData({
        opportunityId,
        stageId: newStageId,
        opportunityTitle: opportunity?.contact_name || 'Contato sem nome'
      });
      setLossReasonDialogOpen(true);
      return false;
    }

    return moveOpportunity(opportunityId, newStageId, orderIndex);
  }, [moveOpportunity, closeOpportunity, reopenOpportunity, opportunities]);

  const handleConfirmLoss = async (lossReasonId: string, lostByUserId?: string) => {
    if (pendingLossData) {
      await closeOpportunity(
        pendingLossData.opportunityId,
        pendingLossData.stageId,
        'lost',
        lossReasonId,
        undefined,
        lostByUserId
      );
      setPendingLossData(null);
    }
  };

  const handleCancelLoss = () => {
    setPendingLossData(null);
    setLossReasonDialogOpen(false);
  };

  const handleConfirmWon = async (wonData: WonOpportunityData) => {
    if (pendingWonData) {
      await closeOpportunity(
        pendingWonData.opportunityId,
        pendingWonData.stageId,
        'won',
        undefined,
        wonData
      );
      setPendingWonData(null);
    }
  };

  const handleCancelWon = () => {
    setPendingWonData(null);
    setWonDialogOpen(false);
  };

  const handleDeleteOpportunity = async (opportunityId: string) => {
    return deleteOpportunity(opportunityId);
  };

  // Carregar pipeline salvo ou usar o primeiro disponível
  useEffect(() => {
    if (pipelines.length > 0 && !selectedPipelineId && user?.account_id) {
      // Tentar recuperar pipeline salvo
      const savedPipelineId = getSelectedPipeline(user.account_id);

      if (savedPipelineId) {
        // Verificar se o pipeline salvo ainda existe
        const pipelineExists = pipelines.some(p => p.id === savedPipelineId);
        if (pipelineExists) {
          console.log('📋 [CRM] Restaurando pipeline salvo:', savedPipelineId);
          setSelectedPipelineId(savedPipelineId);
          return;
        }
      }

      // Se não houver pipeline salvo ou não existir mais, usar o primeiro
      console.log('📋 [CRM] Usando primeiro pipeline disponível');
      setSelectedPipelineId(pipelines[0].id);
      saveSelectedPipeline(user.account_id, pipelines[0].id);
    }
  }, [pipelines, selectedPipelineId, user?.account_id]);

  // Salvar pipeline quando mudar
  const handlePipelineChange = (pipelineId: string) => {
    setSelectedPipelineId(pipelineId);
    if (user?.account_id) {
      console.log('💾 [CRM] Salvando pipeline selecionado:', pipelineId);
      saveSelectedPipeline(user.account_id, pipelineId);
    }
  };

  const pipelineStages = stages.filter(s => s.pipeline_id === selectedPipelineId && s.is_active);

  return (
    <div className="relative">
      <CrmHeader
        pipelines={pipelines}
        selectedPipelineId={selectedPipelineId}
        onPipelineChange={handlePipelineChange}
        onSearchChange={setSearchTerm}
        searchTerm={searchTerm}
        currentFilters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        dateFilter={dateFilter}
        onDateFilterChange={handleDateFilterChange}
        loadingProgress={loadingProgress}
      />

      {loadingProgress.isLoadingMore && loadingProgress.total > 0 && (
        <div className="mb-4">
          <LoadingProgress
            loaded={loadingProgress.loaded}
            total={loadingProgress.total}
            isLoadingMore={loadingProgress.isLoadingMore}
          />
        </div>
      )}

      <CrmMain
        stages={pipelineStages}
        opportunities={opportunities}
        loading={pipelinesLoading || opportunitiesLoading}
        onMoveOpportunity={handleMoveOpportunity}
        onDeleteOpportunity={handleDeleteOpportunity}
        onUpdateOpportunity={updateOpportunity}
        onReorderOpportunities={reorderOpportunities}
        selectedPipelineId={selectedPipelineId}
        searchTerm={searchTerm}
        filters={filters}
        statusFilter={statusFilter}
      />

      <button
        onClick={() => setDialogOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center z-50 group dark:shadow-lg"
        aria-label={t("contact.addOpportunity")}
      >
        <Plus size={24} className="group-hover:rotate-90 transition-transform duration-200" />
      </button>

      <AddOpportunityDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        selectedPipelineId={selectedPipelineId}
        pipelines={pipelines}
        stages={stages}
        onPipelineChange={handlePipelineChange}
        createOpportunity={createOpportunity}
        findExistingOpportunities={findExistingOpportunities}
      />

      <LossReasonDialog
        open={lossReasonDialogOpen}
        onClose={handleCancelLoss}
        onConfirm={handleConfirmLoss}
        opportunityTitle={pendingLossData?.opportunityTitle}
      />

      <WonOpportunityDialog
        open={wonDialogOpen}
        onClose={handleCancelWon}
        onConfirm={handleConfirmWon}
        opportunityTitle={pendingWonData?.opportunityTitle}
        initialProduct={pendingWonData?.initialProduct}
        initialPrice={pendingWonData?.initialPrice}
      />
    </div>
  );
};

export default Crm;
